import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

interface AdMinistries {
  renames: { key: string; from: string; to: string; done: boolean }[];
  creates: { key: string; name: string; description: string; done: boolean }[];
  canApply: boolean;
}

/** Asambleas de Dios · los ministerios con los nombres del Concilio y los que faltan, en un clic. */
export function AdMinistriesPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const url = `/organization/churches/${churchId}/ad/ministries`;
  const data = useAsync((signal) => (churchId ? api.get<AdMinistries>(url, signal) : Promise.resolve(null)), [url]);
  const [rename, setRename] = useState(true);
  const [create, setCreate] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const loaded = data.status === 'ready' ? data.data : null;
  useEffect(() => {
    if (loaded) setCreate(loaded.creates.filter((c) => !c.done).map((c) => c.key));
  }, [loaded]);

  if (!churchId) return <div className="page"><EmptyState title="Elige primero una iglesia en el selector de arriba." /></div>;
  const pendingRenames = loaded ? loaded.renames.filter((r) => !r.done) : [];
  const pendingCreates = loaded ? loaded.creates.filter((c) => !c.done) : [];
  const nothing = loaded && pendingRenames.length === 0 && pendingCreates.length === 0;

  const doRename = rename && pendingRenames.length > 0;
  const chosen = pendingCreates.filter((c) => create.includes(c.key));
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const examples = (names: string[]) => (names.length ? ` (${names.slice(0, 1).join('')}${names.length > 1 ? '…' : ''})` : '');
  const summary = [
    doRename ? `Se cambiará el nombre de ${plural(pendingRenames.length, 'grupo', 'grupos')}${examples(pendingRenames.map((r) => `${r.from} → ${r.to}`))}` : null,
    chosen.length ? `${doRename ? 's' : 'S'}e ${chosen.length === 1 ? 'creará' : 'crearán'} ${plural(chosen.length, 'ministerio', 'ministerios')}${examples(chosen.map((c) => c.name))}` : null,
  ]
    .filter(Boolean)
    .join(' y ');

  async function apply() {
    setConfirming(false);
    setBusy(true);
    setMsg(null);
    try {
      await api.post(url, { rename: doRename, create });
      setMsg({ tone: 'success', text: 'Listo: tus ministerios ya están como en el Concilio.' });
      data.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <p className="eyebrow">Asambleas de Dios</p>
        <h1>Ministerios del Concilio</h1>
        <p className="muted">Organiza tu iglesia con los nombres y ministerios de las Asambleas de Dios. Lo que ya tienes (miembros, líderes, reuniones) se conserva.</p>
      </header>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {loaded ? (
        <>
          {loaded.renames.length ? (
            <Card title="Nombres de los grupos">
              <ul className="list">
                {loaded.renames.map((r) => (
                  <li key={r.key} className="list__item">
                    <span>
                      {r.done ? <strong>{r.to}</strong> : <>{r.from} → <strong>{r.to}</strong></>}
                    </span>
                    {r.done ? <span className="badge badge--active">Listo</span> : null}
                  </li>
                ))}
              </ul>
              {pendingRenames.length && loaded.canApply ? (
                <label className="check">
                  <input type="checkbox" checked={rename} onChange={(e) => setRename(e.target.checked)} /> Cambiar a los nombres del Concilio
                </label>
              ) : null}
            </Card>
          ) : null}

          <Card title="Ministerios del Concilio">
            <ul className="list">
              {loaded.creates.map((c) => (
                <li key={c.key} className="list__item">
                  {c.done || !loaded.canApply ? (
                    <span>
                      <strong>{c.name}</strong> <span className="small muted">— {c.description}</span>
                    </span>
                  ) : (
                    <label className="check">
                      <input
                        type="checkbox"
                        checked={create.includes(c.key)}
                        onChange={(e) => setCreate((v) => (e.target.checked ? [...v, c.key] : v.filter((k) => k !== c.key)))}
                      />{' '}
                      <span>
                        <strong>{c.name}</strong> <span className="small muted">— {c.description}</span>
                      </span>
                    </label>
                  )}
                  {c.done ? <span className="badge badge--active">Ya lo tienes</span> : null}
                </li>
              ))}
            </ul>
          </Card>

          {nothing ? (
            <p className="home-ok">✓ Tu iglesia ya tiene todos los ministerios del Concilio.</p>
          ) : loaded.canApply ? (
            confirming ? (
              <div className="notice" role="alertdialog" aria-label="Confirmar cambios">
                <p>{summary}. ¿Continuar?</p>
                <div className="actions">
                  <Button busy={busy} onClick={() => void apply()}>
                    Sí, aplicar
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirming(false)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="actions">
                <Button busy={busy} disabled={!doRename && chosen.length === 0} onClick={() => setConfirming(true)}>
                  Aplicar
                </Button>
              </div>
            )
          ) : (
            <p className="small muted">Solo la administración de la iglesia puede aplicar estos cambios.</p>
          )}
          <p className="small muted">
            Después, asigna a los líderes de cada uno en <Link to="/app/ministerios">Ministerios</Link>.
          </p>
        </>
      ) : null}
    </div>
  );
}
